import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminSanctionOrderer, adminSearchOrderers } from '../../api/payment/brand-sanction.api';
import { apiErrorMessage } from '../../utils/api-error-message';
import { sanctionLevelLabel } from '../../utils/brand-sanction';
import { AdminOrdererLabel } from '../brand-deal/orderer-badge';
import AdminSanctionForm from './admin-sanction-form';

/** Tab "Xử phạt người đặt đơn": tìm theo tên, mã số thuế hoặc email chủ tài khoản, chọn một người rồi phạt. */
const AdminSanctionPanel: React.FC = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<AdminSanctionOrderer[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<AdminSanctionOrderer | null>(null);
  const [formKey, setFormKey] = useState(0);

  const search = async () => {
    const text = q.trim();
    if (text.length < 2) {
      toast.error('Nhập ít nhất 2 ký tự để tìm.');
      return;
    }
    try {
      setSearching(true);
      setResults(await adminSearchOrderers(text));
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Không tìm được người đặt đơn.'));
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <input
          aria-label="Tìm người đặt đơn"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tên, mã số thuế hoặc email chủ tài khoản"
          className="min-w-[240px] flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        />
        <button type="submit" disabled={searching} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
          Tìm
        </button>
      </form>

      {results !== null &&
        (results.length === 0 ? (
          <p className="text-sm text-gray-500">Không có người đặt đơn nào khớp.</p>
        ) : (
          <div className="space-y-2">
            {results.map((r) => (
              <div
                key={r.adAccountId}
                data-testid="orderer-result"
                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm dark:bg-boxdark ${
                  selected?.adAccountId === r.adAccountId ? 'border-blue-600' : 'border-gray-200 bg-white dark:border-gray-700'
                }`}
              >
                <div className="min-w-0">
                  <div className="font-semibold dark:text-white">
                    <AdminOrdererLabel name={r.businessName} kind={r.ordererKind} ownerIdentityName={r.ownerIdentityName} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {[r.taxCode ? `Mã số thuế ${r.taxCode}` : null, r.ownerEmail].filter(Boolean).join(' · ')}
                  </div>
                  {r.activeLevel && (
                    <div className="text-xs font-semibold text-rose-600 dark:text-rose-300">Đang chịu: {sanctionLevelLabel(r.activeLevel)}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(r);
                    setFormKey((k) => k + 1);
                  }}
                  className="rounded border border-blue-600 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700"
                >
                  Chọn
                </button>
              </div>
            ))}
          </div>
        ))}

      {selected && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-boxdark">
          <AdminSanctionForm
            key={`${selected.adAccountId}-${formKey}`}
            adAccountId={selected.adAccountId}
            onDone={() => setFormKey((k) => k + 1)}
            onCancel={() => setSelected(null)}
            cancelLabel="Đóng"
          />
        </div>
      )}
    </div>
  );
};

export default AdminSanctionPanel;
